import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ChatSessionResponse,
  PaginatedMessagesResponse,
  PaginationParams,
  acceptPlan,
  answerQuestions,
  deleteAIBuilderHistory,
  interruptAIBuilder,
  loadAIBuilderChatSession,
  loadAIBuilderMessages,
  loadAIBuilderNewMessages,
  runAIBuilder,
  transcribeAudio,
  updateSessionMode,
} from './requests';

export const getAiBuilderMessagesQueryKey = (journeyShortId?: string) => [
  'ai-builder-messages',
  journeyShortId,
];

export const getAiBuilderSessionQueryKey = (journeyShortId?: string) => [
  'ai-builder-chat-session',
  journeyShortId,
];

export const useInvalidateAiBuilderMessages = () => {
  const queryClient = useQueryClient();

  const invalidateMessages = (journeyShortId: string) => {
    queryClient.invalidateQueries(getAiBuilderMessagesQueryKey(journeyShortId));
  };

  return {
    invalidateMessages,
  };
};

export const useRunAIBuilder = () => {
  return useMutation({
    mutationFn: runAIBuilder,
  });
};

export const useInterruptAIBuilder = () => {
  return useMutation({
    mutationFn: interruptAIBuilder,
  });
};

export const useAIBuilderMessagesInfinite = (journeyShortId: string) => {
  return useInfiniteQuery({
    queryKey: getAiBuilderMessagesQueryKey(journeyShortId),
    queryFn: ({ pageParam }) =>
      loadAIBuilderMessages(journeyShortId, pageParam as PaginationParams | undefined),
    getNextPageParam: (lastPage: PaginatedMessagesResponse) =>
      lastPage.hasMore ? { beforeId: lastPage.oldestMessageId ?? undefined } : undefined,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useAIBuilderChatSession = (journeyShortId: string) => {
  return useQuery({
    queryKey: getAiBuilderSessionQueryKey(journeyShortId),
    queryFn: () => loadAIBuilderChatSession(journeyShortId),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useAIBuilderNewMessages = () => {
  return useMutation({
    mutationFn: loadAIBuilderNewMessages,
  });
};

export type DeleteAIBuilderHistoryParams = {
  journeyShortId: string;
};

export const useDeleteAIBuilderHistory = () => {
  return useMutation({
    mutationFn: ({ journeyShortId }: DeleteAIBuilderHistoryParams) =>
      deleteAIBuilderHistory(journeyShortId),
  });
};

export const useTranscribeAudio = () => {
  return useMutation({
    mutationFn: transcribeAudio,
  });
};

export const useAnswerQuestions = () => {
  return useMutation({
    mutationFn: answerQuestions,
  });
};

export const useUpdateSessionMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSessionMode,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        getAiBuilderSessionQueryKey(variables.journeyShortId),
        (old: ChatSessionResponse | undefined) => {
          if (!old) {
            return {
              shortId: data.shortId,
              mode: data.mode,
              isGenerating: false,
              loadingLabel: null,
            };
          }
          return {
            ...old,
            shortId: data.shortId,
            mode: data.mode,
          };
        }
      );
    },
  });
};

export const useAcceptPlan = () => {
  const queryClient = useQueryClient();
  const { invalidateMessages } = useInvalidateAiBuilderMessages();

  return useMutation({
    mutationFn: acceptPlan,
    onSuccess: (_, variables) => {
      invalidateMessages(variables.journeyShortId);
      queryClient.invalidateQueries(getAiBuilderSessionQueryKey(variables.journeyShortId));
    },
  });
};
